import { Skeleton } from '@progress/kendo-react-indicators';
import { provideLocalizationService, registerForLocalization } from '@progress/kendo-react-intl';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import React from 'react';
import { connect } from 'react-redux';
import { StudyPlanCycleStudentDepartmentsList } from '.';
import { withRouter } from '../../../components/withRouter';
import { NumericBox, OverviewPage, StackLayout, Text } from '../../../ui';
import { getStudentCycleStatusColor } from '../../../utils/studyPlanCycleDepartmentStudentStatusColors';
import { InlineBadge, ValueItem } from '../../shared/components';
import { loadStudyPlanCycleStudentOverviewPage, toggleStudyPlanCycleStudentOverviewAccreditmentForm, toggleStudyPlanCycleStudentOverviewConfirmDialog, accreditStudyPlanCycleStudentOverview } from '../actions';
import { Button } from '@progress/kendo-react-buttons';
import FormDialog from '../../../ui/components/application/form-dialog';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Field } from '@progress/kendo-react-form';
import { Icon } from '@progress/kendo-react-common';
import styled from 'styled-components';

const CreditsContainer = styled.div`
  display: flex;
`;

const AccreditationIcon = styled(Icon)`
  cursor: pointer;
`;

class StudyPlanCycleStudentOverviewPage extends React.PureComponent {
  constructor() {
    super();
    this.handleBackClick = this.handleBackClick.bind(this);
    this.handleBreadcrumClick = this.handleBreadcrumClick.bind(this);
    this.validateAccreditmentDialogValidate = this.validateAccreditmentDialogValidate.bind(this);
  }

  async componentDidMount() {
    this.props.loadStudyPlanCycleStudentOverviewPage({
      studyPlanId: this.props.params.studyPlanId,
      studyPlanCycleId: this.props.params.studyPlanCycleId,
      studyPlanCycleStudentId: this.props.params.studyPlanCycleStudentId
    });
  }

  render() {
    const { loading } = this.props;
    const localizationService = provideLocalizationService(this);
    const shouldShowAccreditmentInfo = this.props.studyPlanCycleStudent?.accreditedCredits > 0;
    return (
      <OverviewPage
        title={loading
          ? <Skeleton shape='text' style={{ width: 300, height: 28 }}/>
          : this.props.studyPlanCycleStudent?.studentFullName
        }
        breadcrumbItems={[
          {
            id: 'study-plans',
            text: localizationService.toLanguageString('StudyPlanCycle.studyPlans')
          },
          {
            id: 'study-plan',
            text: localizationService.toLanguageString('StudyPlanCycle.studyPlan')
          },
          {
            id: 'study-plan-cycle',
            text: localizationService.toLanguageString('StudyPlanCycle.studyPlanCycle')
          },
          {
            id: 'study-plan-cycle-student',
            text: localizationService.toLanguageString('StudyPlanCycle.studyPlanCycleStudent'),
            disabled: true
          }
        ]}
        onBreadcrumbItemClick={this.handleBreadcrumClick}
        onBackClick={this.handleBackClick}
        summary={
          <StackLayout rowGap='8px'>
            <ValueItem
              label={localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.status')}
              labelWidth='130px'
              loading={loading}
            >
              {this.props.studyPlanCycleStudent &&
                <InlineBadge themeColor={getStudentCycleStatusColor(this.props.studyPlanCycleStudent.status)}>
                  {localizationService.toLanguageString(`studyPlanCycleDepartmentStudentStatus.${this.props.studyPlanCycleStudent.status}`)}
                </InlineBadge>}
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.cycle')}
              labelWidth='130px'
              loading={loading}
            >
              {this.props.studyPlanCycleStudent?.cycleName}
            </ValueItem>
            <ValueItem
              label={shouldShowAccreditmentInfo
                ? localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.creditsWithAccreditment')
                : localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.credits')
              }
              labelWidth='130px'
              loading={loading}
            >
              <CreditsContainer>
                {shouldShowAccreditmentInfo
                  ? `${this.props.studyPlanCycleStudent?.credits} (${this.props.studyPlanCycleStudent?.accreditedCredits})`
                  : this.props.studyPlanCycleStudent?.credits
                }
                {this.props.studyPlanCycleStudent?.cycleCredits - this.props.studyPlanCycleStudent?.credits > 0 &&
                  <AccreditationIcon
                    name='import'
                    onClick={this.props.toggleStudyPlanCycleStudentOverviewAccreditmentForm}
                  />
                }
              </CreditsContainer>
            </ValueItem>
            <ValueItem
              label={localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.cycleManager')}
              labelWidth='130px'
              loading={loading}
            >
              {this.props.studyPlanCycleStudent?.cycleManagerFullName}
            </ValueItem>
          </StackLayout>
        }
        tabs={
          <TabStrip selected={this.props.selectedTab}>
            <TabStripTab title={localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.studentDepartments')}/>
          </TabStrip>
        }
      >
        {this.props.selectedTab === 0 &&
          <StudyPlanCycleStudentDepartmentsList
            studyPlanId={this.props.params.studyPlanId}
            studyPlanCycleId={this.props.params.studyPlanCycleId}
            studyPlanCycleStudentId={this.props.params.studyPlanCycleStudentId}
          />
        }
        {this.props.isAccreditmentFormVisible &&
          <FormDialog
            title={localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.accredit')}
            loading={this.props.loading}
            width={'325px'}
            onSubmit={e =>
              e.accreditedCredits + this.props.studyPlanCycleStudent?.credits == this.props.studyPlanCycleStudent.cycleCredits &&
              (this.props.studyPlanCycleStudent.dateTo ? new Date(this.props.studyPlanCycleStudent.dateTo) < new Date() : true)
                ? this.props.toggleStudyPlanCycleStudentOverviewConfirmDialog(e)
                : this.props.accreditStudyPlanCycleStudentOverview(e)
            }
            onCancel={this.props.toggleStudyPlanCycleStudentOverviewAccreditmentForm}
            validator={(values) => this.validateAccreditmentDialogValidate(values, localizationService)}
            validationResult={this.props.accreditationValidationResult}
            errorMessage={this.props.accreditationErrorMessage}
            confirmButtonText={localizationService.toLanguageString('buttons.accredit')}
            closeButtonText={localizationService.toLanguageString('buttons.close')}
            render={() => (
              <StackLayout>
                <Field
                  name='accreditedCredits'
                  component={NumericBox}
                  label={localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.credits')}
                  min={0}
                  max={this.props.studyPlanCycleStudent?.cycleCredits - this.props.studyPlanCycleStudent?.credits}
                />
              </StackLayout>
            )}
          />
        }
        {this.props.isConfirmationDialogVisible &&
          <Dialog
            title={localizationService.toLanguageString('custom.confirm')}
            onClose={this.props.toggleStudyPlanCycleStudentOverviewConfirmDialog}
            width={'325px'}
          >
            <Text>{localizationService.toLanguageString('StudyPlanCycleStudentOverviewPage.accreditmentConfirmationDialogText')}</Text>
            <DialogActionsBar>
              <Button
                onClick={this.props.accreditStudyPlanCycleStudentOverview}
                themeColor='primary'
              >
                {localizationService.toLanguageString('buttons.confirm')}
              </Button>
              <Button onClick={this.props.toggleStudyPlanCycleStudentOverviewConfirmDialog}>{localizationService.toLanguageString('buttons.cancel')}</Button>
            </DialogActionsBar>
          </Dialog>
        }
      </OverviewPage>
    );
  }

  validateAccreditmentDialogValidate(values, localizationService) {
    const errors = {};
    if (values.accreditedCredits === undefined) {
      errors.accreditedCredits = localizationService.toLanguageString('validation.required');
    } else if (values.accreditedCredits < 0) {
      errors.accreditedCredits = localizationService.toLanguageString('validation.negativeValue');
    }
    return errors;
  }

  handleBackClick() {
    this.props.navigate(-1);
  }

  handleBreadcrumClick(e) {
    if (e.id === 'study-plans') {
      this.props.navigate('/study_plans');
    } else if (e.id === 'study-plan') {
      this.props.navigate(`/study_plans/${this.props.params.studyPlanId}/overview`);
    } else if (e.id === 'study-plan-cycle') {
      this.props.navigate(`/study_plans/${this.props.params.studyPlanId}/cycles/${this.props.params.studyPlanCycleId}/overview`);
    }
  }
}

const mapStateToProps = (state) => ({
  loading: state.studyPlanCycleStudentOverviewPage.loading,
  studyPlanId: state.studyPlanCycleStudentOverviewPage.studyPlanId,
  studyPlanCycleId: state.studyPlanCycleStudentOverviewPage.studyPlanCycleId,
  studyPlanCycleStudentId: state.studyPlanCycleStudentOverviewPage.studyPlanCycleStudentId,
  studyPlanCycleStudent: state.studyPlanCycleStudentOverviewPage.studyPlanCycleStudent,
  studyPlanCycleStudentDepartments: state.studyPlanCycleStudentOverviewPage.studyPlanCycleStudentDepartments,
  selectedTab: state.studyPlanCycleStudentOverviewPage.selectedTab,
  isAccreditmentFormVisible: state.studyPlanCycleStudentOverviewPage.isAccreditmentFormVisible,
  isConfirmationDialogVisible: state.studyPlanCycleStudentOverviewPage.isConfirmationDialogVisible
});

const mapDispatchToProps = (dispatch) => ({
  loadStudyPlanCycleStudentOverviewPage: (payload) => dispatch(loadStudyPlanCycleStudentOverviewPage(payload)),
  toggleStudyPlanCycleStudentOverviewAccreditmentForm: () => dispatch(toggleStudyPlanCycleStudentOverviewAccreditmentForm()),
  toggleStudyPlanCycleStudentOverviewConfirmDialog: (payload) => dispatch(toggleStudyPlanCycleStudentOverviewConfirmDialog(payload)),
  accreditStudyPlanCycleStudentOverview: (payload) => dispatch(accreditStudyPlanCycleStudentOverview(payload))
});

registerForLocalization(StudyPlanCycleStudentOverviewPage);

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(StudyPlanCycleStudentOverviewPage));